// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import { object, func } from 'prop-types';
import uniqueId from 'lodash/uniqueId';
import { useTranslation } from 'react-i18next';
import { stringify } from 'query-string';
import omit from 'lodash/omit';

import ActiveFiltersComponent from 'Components/Filters/ActiveFilters';
import ActiveFilter from 'Components/Filters/ActiveFilter';
import useRouter from 'Hooks/useRouter';

const queryFilters = [
  'only_promotional',
  'only_resealed',
  'only_warehouse_products',
  'eol',
  'only_available',
  'include_reserved_stock',
  'stock-clearing-sale'
];

const ActiveFilters = ({ onClear, activeFilters, onChange }) => {
  const { location, push, query } = useRouter();
  const { t } = useTranslation();

  const handleOnRemove = (key, value) => {
    onChange(key, value)(false, true);
  };

  const handleOnRemoveQueryFilter = key => {
    // Remove both filters
    if (key === 'only_available') {
      push({
        pathname: location.pathname,
        search: stringify(omit(query || {}, ['only_available', 'include_reserved_stock']))
      });
    } else {
      push({
        pathname: location.pathname,
        search: stringify(omit(query || {}, [key]))
      });
    }
  };

  const allowedQueryFiltersComponent = useMemo(
    () => Object
      .entries(query)
      .filter(([key]) => queryFilters.includes(key))
      .map(([slug]) => (
        <ActiveFilter
          key={ uniqueId(slug) }
          filterType={ slug }
          isQueryFilter
          activeFilter={ { slug, value: t(`filters.${ slug }`) } }
          onRemove={ handleOnRemoveQueryFilter }
        />
      )),
    [query]
  );

  const activeFiltersComponent = useMemo(
    () => Object
      .entries(activeFilters)
      .filter(([slug, activeFilter]) => {
        if (slug === 'price') {
          // eslint-disable-next-line no-restricted-globals
          return !isNaN(activeFilter[0]) && !isNaN(activeFilter[1]);
        }
        return Array.isArray(activeFilter) ? activeFilter.length : !!activeFilter;
      })
      .map(([slug, activeFilter]) => {
        if (slug === 'price') {
          // eslint-disable-next-line no-param-reassign
          activeFilter = {
            // eslint-disable-next-line no-restricted-globals
            min: !isNaN(activeFilter[0]) && activeFilter[0],
            // eslint-disable-next-line no-restricted-globals
            max: !isNaN(activeFilter[1]) && activeFilter[1]
          };
        }
        return (
          <ActiveFilter
            key={ uniqueId(slug) }
            filterType={ slug }
            activeFilter={ activeFilter }
            onRemove={ handleOnRemove }
          />
        );
      }),
    [activeFilters]
  );

  const children = [];

  if (allowedQueryFiltersComponent.length) {
    children.push(...allowedQueryFiltersComponent);
  }
  if (activeFiltersComponent.length) {
    children.push(...activeFiltersComponent);
  }

  if (!children.length) return null;

  return (
    <ActiveFiltersComponent onClear={ onClear }>
      { React.Children.toArray(children) }
    </ActiveFiltersComponent>
  );
};

ActiveFilters.propTypes = {
  onClear: func,
  activeFilters: object,
  onChange: func
};

export default ActiveFilters;
